import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

import {
  setDatadogGlobalContextProperty,
  removeDatadogGlobalContextProperty,
} from '../services/telemetry/datadog';
import { safeTelemetryPreferenceEntries } from '../services/telemetry/telemetryPreferences';

// Dispatched by AuthContext on every logout / auth-clear path. UserContext
// resets its session-scoped state in response so (a) the previous user's
// site/preferences can't leak into the next session and (b) the Datadog global
// context — which AuthContext wipes wholesale on logout — is re-hydrated from
// state on the next login even when the new session reuses the same site
// (the reset routes state through '' first, so the setter is always a change
// that re-fires the mirror effects).
export const SESSION_CLEARED_EVENT = 'b2o:session-cleared';

const writeOrClearDatadogContext = (key: string, value: string): void => {
  if (value) setDatadogGlobalContextProperty(key, value);
  else removeDatadogGlobalContextProperty(key);
};

// A pre-fix session's setSiteSegment(undefined) persisted the literal string
// "undefined" (Storage.setItem stringifies its value) — treat that the same
// as a missing key so an already-affected session self-heals on next load,
// instead of rehydrating the corrupted value forever (1011251).
const readSanitizedSiteValue = (key: string): string => {
  const saved = localStorage.getItem(key);
  return saved && saved !== 'undefined' ? saved : '';
};

interface UserContextType {
  appId: string;
  siteCode: string;
  siteSegment: string;
  siteName: string;
  setAppId: (id: string) => void;
  // Inputs are optional because they're sourced from an untyped CustomEvent
  // payload (SelectSiteModule) that can omit any field (1011251) — the state
  // itself stays a plain string, since every setter coerces before setState.
  setSiteCode: (siteCode: string | undefined) => void;
  setSiteSegment: (siteSegment: string | undefined) => void;
  setSiteName: (siteName: string | undefined) => void;
  userPreferences: Record<string, any>;
  setUserPreference: (key: string, value: any) => void;
}

const UserContext = createContext<UserContextType>({
  appId: '',
  siteCode: '',
  siteSegment: '',
  siteName: '',
  setAppId: () => {},
  setSiteCode: () => {},
  setSiteSegment: () => {},
  setSiteName: () => {},
  userPreferences: {},
  setUserPreference: () => {},
});

interface UserProviderProps {
  children: ReactNode;
  initialAppId?: string;
}

export const UserProvider = ({
  children,
  initialAppId = '',
}: UserProviderProps): React.JSX.Element => {
  const [appId, setAppIdState] = useState<string>(initialAppId);
  const [siteCode, setSiteCodeState] = useState<string>(() =>
    readSanitizedSiteValue('b2o-selected-site-code'),
  );
  const [siteSegment, setSiteSegmentState] = useState<string>(() =>
    readSanitizedSiteValue('b2o-selected-site-segment'),
  );
  const [siteName, setSiteNameState] = useState<string>(() =>
    readSanitizedSiteValue('b2o-selected-site-name'),
  );

  const [userPreferences, setUserPreferences] = useState<Record<string, any>>({});

  const setAppId = (id: string) => {
    setAppIdState(id);
  };

  const setUserPreference = (key: string, value: any) => {
    setUserPreferences((prev) => {
      const updated = { ...prev, [key]: value };
      return updated;
    });
  };

  const setSiteCode = (code: string | undefined) => {
    // Coerce so consumers (e.g. useCountryDefaultLanguage's siteCode.slice(0, 2))
    // never see undefined (1011251).
    setSiteCodeState(code ?? '');
  };

  const setSiteSegment = (segment: string | undefined) => {
    // Coerce before setState/persist so "undefined" is never written to
    // storage (1011251).
    const value = segment ?? '';
    setSiteSegmentState(value);
    localStorage.setItem('b2o-selected-site-segment', value);
  };

  const setSiteName = (name: string | undefined) => {
    // Coerce before setState/persist, same as setSiteSegment (1011251).
    const value = name ?? '';
    setSiteNameState(value);
    localStorage.setItem('b2o-selected-site-name', value);
  };

  // Mirror identifiers into the Datadog global context so every RUM event
  // and Log can be filtered by site & app. Empty values are REMOVED rather
  // than written as '' — otherwise pre-login state leaks into RUM as
  // `app.id: ""` and dashboards lose the "is the user signed in yet?"
  // distinction. Safe no-ops when DD isn't initialized.
  useEffect(() => {
    writeOrClearDatadogContext('app.id', appId);
  }, [appId]);
  useEffect(() => {
    writeOrClearDatadogContext('site.code', siteCode);
  }, [siteCode]);
  useEffect(() => {
    writeOrClearDatadogContext('site.segment', siteSegment);
  }, [siteSegment]);

  // Flatten user preferences onto Datadog as individual `user.<key>` global
  // context properties (matching useLogger.ts' per-call baseProperties shape).
  // A single nested `user.preferences` blob would (a) inflate every event,
  // (b) require nested-attribute syntax in DD facets, and (c) double-attach
  // the same data because useLogger already flattens it per-call.
  //
  // userPreferences is Record<string, any>, so we do NOT mirror it verbatim:
  // safeTelemetryPreferenceEntries applies a shared allowlist + scalar
  // sanitization so an arbitrary (or future PII/token/free-text) preference
  // can never leak onto every RUM event / Log in a third-party sink.
  //
  // Keys that disappear between renders — or that stop being safe — must also
  // be REMOVED from the context, otherwise stale preferences from a previous
  // user/session would continue to ride along on every event. previousPrefKeys
  // tracks what we actually wrote last so we can compute the diff.
  const previousPrefKeys = useRef<Set<string>>(new Set());
  useEffect(() => {
    const safeEntries = safeTelemetryPreferenceEntries(userPreferences);
    const currentKeys = new Set(safeEntries.map(([key]) => key));

    for (const key of previousPrefKeys.current) {
      if (!currentKeys.has(key)) {
        removeDatadogGlobalContextProperty(`user.${key}`);
      }
    }

    for (const [key, value] of safeEntries) {
      setDatadogGlobalContextProperty(`user.${key}`, value);
    }

    previousPrefKeys.current = currentKeys;
  }, [userPreferences]);

  // On logout / auth-clear, reset session-scoped state. This clears the stale
  // user's site & preferences from React state (so the next session can't
  // inherit them) which, via the mirror effects above, also removes
  // site.*/user.* from the Datadog global context. app.id is app-level (not
  // session state) and AuthContext's clear wipes the WHOLE DD global context,
  // so we re-assert it here so anonymous / next-session events keep the app
  // identifier. Site selection on the next login re-adds site.* through the
  // mirror (state goes '' -> value = a change), re-hydrating the context.
  useEffect(() => {
    const handleSessionCleared = (): void => {
      setSiteCodeState('');
      setSiteSegmentState('');
      setSiteNameState('');
      setUserPreferences({});
      // Also drop the persisted site selection. setSiteSegment/setSiteName write
      // these keys and the state initializers re-read them on the next page load,
      // so without this a logout+reload would re-hydrate the previous session's
      // site into state AND back into the Datadog global context (site.segment),
      // mis-attributing anonymous / next-user events to the old site.
      localStorage.removeItem('b2o-selected-site-code');
      localStorage.removeItem('b2o-selected-site-segment');
      localStorage.removeItem('b2o-selected-site-name');
      if (appId) setDatadogGlobalContextProperty('app.id', appId);
    };
    document.addEventListener(SESSION_CLEARED_EVENT, handleSessionCleared);
    return () => {
      document.removeEventListener(SESSION_CLEARED_EVENT, handleSessionCleared);
    };
  }, [appId]);

  return (
    <UserContext.Provider
      value={{
        appId,
        setAppId,
        userPreferences,
        setUserPreference,
        setSiteCode,
        siteCode,
        siteSegment,
        setSiteSegment,
        siteName,
        setSiteName,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
